import Phaser from 'phaser';
import { TileLayout, PlacedObject, ObjectType } from '../../../../shared/types/tileLayout';
import { TILE_SIZE } from '../types';

export interface TileEditorConfig {
  layoutId?: string;
  onSave?: (layout: TileLayout) => void;
  onClose?: () => void;
}

export class TileEditorScene extends Phaser.Scene {
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private objectsContainer!: Phaser.GameObjects.Container;
  private currentLayout: TileLayout | null = null;
  private placedObjects: Map<string, Phaser.GameObjects.Graphics> = new Map();
  
  private selectedObjectType: ObjectType | null = null;
  private selectedObjectId: string | null = null;
  private gridVisible: boolean = true;
  private currentTool: 'place' | 'erase' | 'select' | 'pan' = 'place';
  
  private config: TileEditorConfig = {};
  
  private cameraDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;

  constructor() {
    super({ key: 'TileEditorScene' });
  }

  public init(config: TileEditorConfig): void {
    this.config = config;
  }

  public create(): void {
    this.objectsContainer = this.add.container(0, 0);
    this.gridGraphics = this.add.graphics();
    
    this.cameras.main.setBounds(0, 0, 10000, 10000);
    this.cameras.main.setZoom(1);
    
    if (this.config.layoutId) {
      this.loadLayout(this.config.layoutId);
    } else {
      this.createNewLayout();
    }
    
    this.setupCameraControls();
    this.setupInput();
    this.renderGrid();
    
    this.events.on('layout:update', this.onLayoutUpdate, this);
    this.events.on('tool:change', this.onToolChange, this);
    this.events.on('object:select', this.onObjectSelect, this);
    this.events.on('grid:toggle', this.toggleGrid, this);
  }

  private createNewLayout(): void {
    this.currentLayout = {
      id: crypto.randomUUID(),
      name: 'New Layout',
      version: 1,
      width: 200,
      height: 200,
      tileSize: TILE_SIZE,
      objects: [],
      metadata: {
        author: 'admin',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        target: 'main_ocean',
      },
    };
  }

  private loadLayout(layoutId: string): void {
    fetch(`/api/admin/dev/tile-editor/layouts/${layoutId}`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'ok' && data.layout) {
          this.currentLayout = data.layout;
          this.renderAllObjects();
          this.events.emit('layout:loaded', this.currentLayout);
        }
      })
      .catch(err => {
        console.error('[TileEditor] Failed to load layout:', err);
        this.createNewLayout();
      });
  }

  private setupCameraControls(): void {
    const camera = this.cameras.main;
    
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, gameObjects: any[], deltaX: number, deltaY: number) => {
      const zoomSpeed = 0.001;
      const newZoom = Phaser.Math.Clamp(camera.zoom - deltaY * zoomSpeed, 0.25, 4);
      camera.setZoom(newZoom);
      this.renderGrid();
    });
    
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.middleButtonDown() || (this.currentTool === 'pan' && pointer.leftButtonDown())) {
        this.cameraDragging = true;
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
      }
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.cameraDragging) {
        const deltaX = pointer.x - this.dragStartX;
        const deltaY = pointer.y - this.dragStartY;
        
        camera.scrollX -= deltaX / camera.zoom;
        camera.scrollY -= deltaY / camera.zoom;
        
        this.dragStartX = pointer.x;
        this.dragStartY = pointer.y;
      }
    });
    
    this.input.on('pointerup', () => {
      this.cameraDragging = false;
    });
  }

  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.cameraDragging || pointer.middleButtonDown()) {
        return;
      }
      
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const tileX = Math.floor(worldPoint.x / TILE_SIZE);
      const tileY = Math.floor(worldPoint.y / TILE_SIZE);
      
      if (!this.currentLayout) return;
      
      if (tileX < 0 || tileX >= this.currentLayout.width || tileY < 0 || tileY >= this.currentLayout.height) {
        return;
      }
      
      if (this.currentTool === 'place' && this.selectedObjectType) {
        this.placeObject(tileX, tileY, this.selectedObjectType);
      } else if (this.currentTool === 'erase') {
        this.eraseObject(tileX, tileY);
      } else if (this.currentTool === 'select') {
        this.selectObject(tileX, tileY);
      }
    });
    
    this.input.keyboard?.on('keydown-DELETE', () => {
      if (this.selectedObjectId) {
        this.deleteSelectedObject();
      }
    });
  }

  private placeObject(tileX: number, tileY: number, type: ObjectType): void {
    if (!this.currentLayout) return;
    
    const existingIndex = this.currentLayout.objects.findIndex(
      obj => obj.tileX === tileX && obj.tileY === tileY
    );
    
    if (existingIndex !== -1) {
      this.currentLayout.objects.splice(existingIndex, 1);
      const existingId = Array.from(this.placedObjects.entries())
        .find(([_, graphic]) => {
          const data = (graphic as any).data;
          return data && data.values.tileX === tileX && data.values.tileY === tileY;
        })?.[0];
      
      if (existingId) {
        this.placedObjects.get(existingId)?.destroy();
        this.placedObjects.delete(existingId);
      }
    }
    
    const newObject: PlacedObject = {
      id: crypto.randomUUID(),
      type,
      tileX,
      tileY,
      properties: this.getDefaultProperties(type),
      layer: 0,
    };
    
    this.currentLayout.objects.push(newObject);
    this.renderObject(newObject);
    this.events.emit('layout:modified', this.currentLayout);
  }

  private eraseObject(tileX: number, tileY: number): void {
    if (!this.currentLayout) return;
    
    const index = this.currentLayout.objects.findIndex(
      obj => obj.tileX === tileX && obj.tileY === tileY
    );
    
    if (index !== -1) {
      const obj = this.currentLayout.objects[index];
      this.currentLayout.objects.splice(index, 1);
      
      this.placedObjects.get(obj.id)?.destroy();
      this.placedObjects.delete(obj.id);
      
      this.events.emit('layout:modified', this.currentLayout);
    }
  }

  private selectObject(tileX: number, tileY: number): void {
    if (!this.currentLayout) return;
    
    const obj = this.currentLayout.objects.find(
      o => o.tileX === tileX && o.tileY === tileY
    );
    
    if (obj) {
      this.selectedObjectId = obj.id;
      this.highlightSelectedObject(obj.id);
      this.events.emit('object:selected', obj);
    } else {
      this.selectedObjectId = null;
      this.clearHighlights();
      this.events.emit('object:deselected');
    }
  }

  private deleteSelectedObject(): void {
    if (!this.currentLayout || !this.selectedObjectId) return;
    
    const index = this.currentLayout.objects.findIndex(obj => obj.id === this.selectedObjectId);
    
    if (index !== -1) {
      this.currentLayout.objects.splice(index, 1);
      this.placedObjects.get(this.selectedObjectId)?.destroy();
      this.placedObjects.delete(this.selectedObjectId);
      
      this.selectedObjectId = null;
      this.events.emit('layout:modified', this.currentLayout);
      this.events.emit('object:deselected');
    }
  }

  private highlightSelectedObject(objectId: string): void {
    this.clearHighlights();
    
    const graphic = this.placedObjects.get(objectId);
    if (graphic) {
      const data = (graphic as any).data.values;
      const highlight = this.add.graphics();
      highlight.lineStyle(2, 0xffff00, 1);
      highlight.strokeRect(
        data.tileX * TILE_SIZE - 1,
        data.tileY * TILE_SIZE - 1,
        TILE_SIZE + 2,
        TILE_SIZE + 2
      );
      (highlight as any).data = { type: 'highlight' };
      this.objectsContainer.add(highlight);
    }
  }

  private clearHighlights(): void {
    this.objectsContainer.each((child: Phaser.GameObjects.GameObject) => {
      const childData = (child as any).data;
      if (childData?.values?.type === 'highlight') {
        child.destroy();
      }
    });
  }

  private getDefaultProperties(type: ObjectType): Record<string, any> {
    switch (type) {
      case 'island':
        return { radius: 50, biome: 'tropical', isPermanent: false };
      case 'npc':
        return { shipType: 'merchant', behavior: 'patrol', level: 1 };
      case 'resource_node':
        return { resourceType: 'fish', quantity: 10 };
      case 'hazard':
        return { hazardType: 'whirlpool', damage: 10 };
      case 'structure':
        return { structureType: 'dock', name: 'Unnamed Structure' };
      case 'custom':
        return { customType: 'generic' };
      default:
        return {};
    }
  }

  private renderGrid(): void {
    if (!this.currentLayout) return;
    
    this.gridGraphics.clear();
    
    if (!this.gridVisible) return;
    
    const camera = this.cameras.main;
    const zoom = camera.zoom;
    
    const visibleStartX = Math.floor(camera.scrollX / TILE_SIZE);
    const visibleStartY = Math.floor(camera.scrollY / TILE_SIZE);
    const visibleEndX = Math.ceil((camera.scrollX + camera.width / zoom) / TILE_SIZE);
    const visibleEndY = Math.ceil((camera.scrollY + camera.height / zoom) / TILE_SIZE);
    
    const startX = Math.max(0, visibleStartX);
    const startY = Math.max(0, visibleStartY);
    const endX = Math.min(this.currentLayout.width, visibleEndX);
    const endY = Math.min(this.currentLayout.height, visibleEndY);
    
    this.gridGraphics.lineStyle(1, 0x444444, 0.3);
    
    for (let x = startX; x <= endX; x++) {
      this.gridGraphics.lineBetween(
        x * TILE_SIZE,
        startY * TILE_SIZE,
        x * TILE_SIZE,
        endY * TILE_SIZE
      );
    }
    
    for (let y = startY; y <= endY; y++) {
      this.gridGraphics.lineBetween(
        startX * TILE_SIZE,
        y * TILE_SIZE,
        endX * TILE_SIZE,
        y * TILE_SIZE
      );
    }
    
    this.gridGraphics.lineStyle(2, 0x888888, 0.5);
    this.gridGraphics.strokeRect(0, 0, this.currentLayout.width * TILE_SIZE, this.currentLayout.height * TILE_SIZE);
  }

  private renderObject(obj: PlacedObject): void {
    const graphic = this.add.graphics();
    const color = this.getObjectColor(obj.type);
    
    graphic.fillStyle(color, 0.7);
    graphic.fillRect(
      obj.tileX * TILE_SIZE + 2,
      obj.tileY * TILE_SIZE + 2,
      TILE_SIZE - 4,
      TILE_SIZE - 4
    );
    
    graphic.lineStyle(1, 0xffffff, 0.5);
    graphic.strokeRect(
      obj.tileX * TILE_SIZE + 2,
      obj.tileY * TILE_SIZE + 2,
      TILE_SIZE - 4,
      TILE_SIZE - 4
    );
    
    (graphic as any).data = new Phaser.Data.DataManager(graphic);
    (graphic as any).data.set('tileX', obj.tileX);
    (graphic as any).data.set('tileY', obj.tileY);
    (graphic as any).data.set('objectId', obj.id);
    
    this.placedObjects.set(obj.id, graphic);
    this.objectsContainer.add(graphic);
  }

  private renderAllObjects(): void {
    if (!this.currentLayout) return;
    
    this.placedObjects.forEach(graphic => graphic.destroy());
    this.placedObjects.clear();
    
    this.currentLayout.objects.forEach(obj => this.renderObject(obj));
  }

  private getObjectColor(type: ObjectType): number {
    switch (type) {
      case 'island': return 0x2ecc71;
      case 'npc': return 0x3498db;
      case 'resource_node': return 0xf39c12;
      case 'hazard': return 0xe74c3c;
      case 'structure': return 0x9b59b6;
      case 'custom': return 0x95a5a6;
      default: return 0xffffff;
    }
  }

  private toggleGrid(): void {
    this.gridVisible = !this.gridVisible;
    this.renderGrid();
  }

  private onLayoutUpdate(layout: TileLayout): void {
    this.currentLayout = layout;
    this.renderAllObjects();
    this.renderGrid();
  }

  private onToolChange(tool: 'place' | 'erase' | 'select' | 'pan'): void {
    this.currentTool = tool;
  }

  private onObjectSelect(type: ObjectType): void {
    this.selectedObjectType = type;
    this.currentTool = 'place';
  }

  public saveLayout(): void {
    if (!this.currentLayout) return;
    
    fetch('/api/admin/dev/tile-editor/layouts/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.currentLayout),
    })
      .then(res => res.json())
      .then(data => {
        if (data.status === 'ok') {
          console.log('[TileEditor] Layout saved:', data.layout);
          this.currentLayout = data.layout;
          this.config.onSave?.(data.layout);
          this.events.emit('layout:saved', data.layout);
        }
      })
      .catch(err => {
        console.error('[TileEditor] Failed to save layout:', err);
      });
  }

  public getLayout(): TileLayout | null {
    return this.currentLayout;
  }

  public setLayoutName(name: string): void {
    if (this.currentLayout) {
      this.currentLayout.name = name;
      this.events.emit('layout:modified', this.currentLayout);
    }
  }

  public setLayoutDimensions(width: number, height: number): void {
    if (this.currentLayout) {
      this.currentLayout.width = width;
      this.currentLayout.height = height;
      this.renderGrid();
      this.events.emit('layout:modified', this.currentLayout);
    }
  }

  public clearLayout(): void {
    if (this.currentLayout) {
      this.currentLayout.objects = [];
      this.renderAllObjects();
      this.events.emit('layout:modified', this.currentLayout);
    }
  }

  public update(): void {
    const camera = this.cameras.main;
    const zoom = camera.zoom;
    
    const keyboard = this.input.keyboard;
    if (!keyboard) return;
    
    const panSpeed = 10 / zoom;
    
    if (keyboard.addKey('W').isDown || keyboard.addKey('UP').isDown) {
      camera.scrollY -= panSpeed;
    }
    if (keyboard.addKey('S').isDown || keyboard.addKey('DOWN').isDown) {
      camera.scrollY += panSpeed;
    }
    if (keyboard.addKey('A').isDown || keyboard.addKey('LEFT').isDown) {
      camera.scrollX -= panSpeed;
    }
    if (keyboard.addKey('D').isDown || keyboard.addKey('RIGHT').isDown) {
      camera.scrollX += panSpeed;
    }
  }

  public shutdown(): void {
    this.events.off('layout:update');
    this.events.off('tool:change');
    this.events.off('object:select');
    this.events.off('grid:toggle');
    
    this.placedObjects.forEach(graphic => graphic.destroy());
    this.placedObjects.clear();
    
    this.config.onClose?.();
  }
}
